import React, { useState, useEffect } from "react";
import { FormApi } from "apis";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MuiButton from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Modal from "components/modal";
import Button from "components/button";
import TextField from "components/textfield";
import FormAlert from "components/form-alert";
import { useTheme } from "styled-components";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUPPORT_INBOX = "support_inbox";

const initials = (recipient) => {
  const source = recipient.name || recipient.email || "";
  const parts = source.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Confirms who a client-facing launch email goes to, before it goes anywhere.
 *
 * The approval or release behind it is already saved by the time this opens --
 * this dialog only decides the email, and closing it without sending is a
 * first-class outcome, not a cancel.
 */
function LaunchEmailModal({ open = true, communityId, launchEmail, onClose }) {
  const theme = useTheme();

  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newEmailError, setNewEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error");

  // Everyone the back end suggested starts ticked; the admin narrows from there.
  useEffect(() => {
    setRecipients(
      (launchEmail?.recipients ?? []).map((recipient) => ({
        ...recipient,
        selected: true,
        added: false,
      })),
    );
    setNewEmail("");
    setNewEmailError("");
    setMessage("");
    setShowPreview(false);
  }, [launchEmail]);

  const selected = recipients.filter((recipient) => recipient.selected);
  const alwaysCopied = launchEmail?.always_copied ?? [];

  const toggleRecipient = (email) => {
    setRecipients((current) =>
      current.map((recipient) =>
        recipient.email === email
          ? { ...recipient, selected: !recipient.selected }
          : recipient,
      ),
    );
  };

  const setAllSelected = (value) => {
    setRecipients((current) =>
      current.map((recipient) => ({ ...recipient, selected: value })),
    );
  };

  const removeRecipient = (email) => {
    setRecipients((current) =>
      current.filter((recipient) => recipient.email !== email),
    );
  };

  const addRecipient = () => {
    const email = newEmail.trim();

    if (!EMAIL_PATTERN.test(email)) {
      setNewEmailError("Enter a valid email address");
      return;
    }

    const alreadyListed = recipients.some(
      (recipient) => recipient.email.toLowerCase() === email.toLowerCase(),
    );

    if (alreadyListed) {
      setNewEmailError("That address is already on the list");
      return;
    }

    setRecipients((current) => [
      ...current,
      { email, name: "", role: "", source: "", selected: true, added: true },
    ]);
    setNewEmail("");
    setNewEmailError("");
  };

  const sendButtonClicked = () => {
    if (!communityId || selected.length === 0) return;

    setSending(true);
    FormApi.sendLaunchEmail(
      communityId,
      launchEmail.kind,
      selected.map((recipient) => recipient.email),
    ).then(
      (response) => {
        setSending(false);
        if (response?.success) {
          onClose(true, response.sent_count ?? selected.length);
        } else {
          showMessage("Could not send the email. Please try again.", "error");
        }
      },
      () => {
        setSending(false);
        showMessage("Could not send the email. Please try again.", "error");
      },
    );
  };

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
  };

  const actionButtons = [
    {
      id: 1,
      title: "Don't send",
      bgColor: theme.colors.actionGray,
      titleColor: theme.colors.text,
      disabled: sending,
      onClick: () => onClose(false),
    },
    {
      id: 2,
      title:
        selected.length === 1
          ? "Send to 1 recipient"
          : `Send to ${selected.length} recipients`,
      bgColor: theme.colors.green,
      titleColor: theme.colors.white,
      loading: sending,
      disabled: sending || selected.length === 0,
      onClick: sendButtonClicked,
    },
  ];

  return (
    <Modal
      open={open}
      maxWidth='sm'
      title={launchEmail?.title ?? "Send launch email"}
      onClose={sending ? null : () => onClose(false)}
      footerButtons={actionButtons}
    >
      <Box sx={{ px: 1, pb: 1 }}>
        {/* The status change is already committed -- say so plainly, so
            choosing not to send never feels like undoing the approval. */}
        <Stack
          direction='row'
          spacing={1.5}
          alignItems='flex-start'
          sx={{
            backgroundColor: theme.colors.whiteSmoke,
            borderRadius: "8px",
            p: 1.5,
            mb: 2.5,
          }}
        >
          <CheckCircleOutlineIcon
            sx={{ color: theme.colors.green, fontSize: 20, mt: "1px" }}
          />
          <Box>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
              The status change has been saved already.
            </Typography>
            <Typography
              sx={{ fontSize: "0.8125rem", color: theme.colors.gray, mt: 0.25 }}
            >
              {launchEmail?.description} Nothing is emailed until you press
              send, and skipping leaves the status exactly as it is.
            </Typography>
          </Box>
        </Stack>

        {launchEmail?.subject && (
          <Box sx={{ mb: 2.5 }}>
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <MailOutlineIcon
                sx={{ color: theme.colors.gray, fontSize: 20 }}
              />
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: theme.colors.gray,
                  }}
                >
                  Subject
                </Typography>
                <Typography sx={{ fontSize: "0.875rem" }}>
                  {launchEmail.subject}
                </Typography>
              </Box>
              {launchEmail?.body && (
                <MuiButton
                  size='small'
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    whiteSpace: "nowrap",
                  }}
                  endIcon={
                    showPreview ? (
                      <ExpandLessIcon fontSize='small' />
                    ) : (
                      <ExpandMoreIcon fontSize='small' />
                    )
                  }
                  onClick={() => setShowPreview((shown) => !shown)}
                >
                  {showPreview ? "Hide preview" : "Preview email"}
                </MuiButton>
              )}
            </Stack>

            {/* The template is a whole HTML document with its own <style>, so
                it goes in a sandboxed frame rather than into this page. */}
            <Collapse in={showPreview} unmountOnExit>
              <Box
                sx={{
                  mt: 1.5,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <Box
                  component='iframe'
                  title='Email preview'
                  sandbox=''
                  srcDoc={launchEmail.body}
                  sx={{
                    display: "block",
                    width: "100%",
                    height: 320,
                    border: "none",
                    backgroundColor: theme.colors.white,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: theme.colors.gray,
                  mt: 0.75,
                }}
              >
                Each recipient gets this email addressed to them.
              </Typography>
            </Collapse>
          </Box>
        )}

        <Divider sx={{ mb: 1.5 }} />

        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ mb: 0.5 }}
        >
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
            Recipients
            <Typography
              component='span'
              sx={{ fontSize: "0.875rem", color: theme.colors.gray, ml: 0.75 }}
            >
              {selected.length} of {recipients.length} selected
            </Typography>
          </Typography>
          <Stack direction='row' spacing={0.5}>
            <MuiButton
              size='small'
              sx={{ textTransform: "none", fontSize: "0.8125rem", minWidth: 0 }}
              disabled={sending || selected.length === recipients.length}
              onClick={() => setAllSelected(true)}
            >
              Select all
            </MuiButton>
            <MuiButton
              size='small'
              sx={{ textTransform: "none", fontSize: "0.8125rem", minWidth: 0 }}
              disabled={sending || selected.length === 0}
              onClick={() => setAllSelected(false)}
            >
              Clear all
            </MuiButton>
          </Stack>
        </Stack>

        <Box
          sx={{
            border: `1px solid ${theme.colors.border}`,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {recipients.map((recipient, index) => (
            <Stack
              key={recipient.email}
              direction='row'
              alignItems='center'
              spacing={1}
              sx={{
                px: 1,
                py: 1,
                borderTop:
                  index === 0 ? "none" : `1px solid ${theme.colors.border}`,
                backgroundColor: recipient.selected
                  ? theme.colors.white
                  : theme.colors.whiteSmoke,
              }}
            >
              <Checkbox
                size='small'
                checked={recipient.selected}
                disabled={sending}
                onChange={() => toggleRecipient(recipient.email)}
                sx={{
                  color: theme.colors.mediumGray,
                  "&.Mui-checked": { color: theme.colors.green },
                }}
              />
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "0.75rem",
                  bgcolor: recipient.selected
                    ? theme.colors.green
                    : theme.colors.mediumGray,
                }}
              >
                {initials(recipient)}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                {recipient.name && (
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {recipient.name}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    color: theme.colors.gray,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {recipient.email}
                </Typography>
              </Box>
              {recipient.source === SUPPORT_INBOX && (
                <Tooltip title='Pynwheel’s own follow-up inbox'>
                  <Chip
                    size='small'
                    label='Pynwheel'
                    sx={{
                      backgroundColor: theme.colors.thinGray,
                      fontSize: "0.6875rem",
                    }}
                  />
                </Tooltip>
              )}
              {recipient.added && (
                <Chip
                  size='small'
                  label='Added'
                  sx={{
                    backgroundColor: theme.colors.thinGray,
                    fontSize: "0.6875rem",
                  }}
                />
              )}
              {recipient.role && !recipient.added && (
                <Chip
                  size='small'
                  label={recipient.role}
                  variant='outlined'
                  sx={{ fontSize: "0.6875rem" }}
                />
              )}
              {recipient.added && (
                <IconButton
                  size='small'
                  disabled={sending}
                  aria-label={`Remove ${recipient.email}`}
                  onClick={() => removeRecipient(recipient.email)}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Stack>
          ))}

          {recipients.length === 0 && (
            <Typography
              sx={{
                fontSize: "0.8125rem",
                color: theme.colors.gray,
                textAlign: "center",
                py: 2.5,
              }}
            >
              No recipients. Add an address below, or choose “Don’t send”.
            </Typography>
          )}
        </Box>

        <Stack direction='row' spacing={1} alignItems='flex-start' sx={{ mt: 1.5 }}>
          <TextField
            label='Add another recipient'
            placeholder='name@company.com'
            value={newEmail}
            disabled={sending}
            error={Boolean(newEmailError)}
            helperText={newEmailError}
            onChange={(e) => {
              setNewEmail(e.target.value);
              setNewEmailError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addRecipient();
              }
            }}
          />
          <Box sx={{ pt: 0.25 }}>
            <Button
              title='Add'
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              bgColor={theme.colors.secondary}
              titleColor={theme.colors.white}
              disabled={sending || newEmail.trim().length === 0}
              onClick={addRecipient}
            />
          </Box>
        </Stack>

        {alwaysCopied.length > 0 && (
          <Typography
            sx={{ fontSize: "0.75rem", color: theme.colors.gray, mt: 1.5 }}
          >
            {alwaysCopied.join(", ")} is copied on this email automatically.
          </Typography>
        )}

        {selected.length === 0 && recipients.length > 0 && (
          <Typography
            sx={{ fontSize: "0.75rem", color: theme.colors.gray, mt: 1.5 }}
          >
            Nobody is selected. Choose “Don’t send” to finish without emailing
            anyone.
          </Typography>
        )}

        {message && (
          <Box sx={{ mt: 2 }}>
            <FormAlert
              message={message}
              type={messageType}
              onClose={() => setMessage("")}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
}

export default LaunchEmailModal;
